// A chat's knowledge graph as comments over the lanes: a pin where each claim
// rests, floating above everything the plane draws, and a card that reads like
// a comment thread when a pin is opened -- who made each claim and when, what
// it says, the evidence it cites with where each piece sits, a marker for what
// arrived since the reader last looked, and a reply that goes to the chat that
// made the claims. The plane decides where pins go (laneScene's `badges`); the
// graph overlay decides what they hold (traceGraphOverlay.js).
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import HoverTooltip from "./HoverTooltip.jsx";
import Markdown from "./Markdown.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { agoText } from "../lib/traceGraphOverlay.js";

// One hue for everything the graph lays over the lanes (styles.css --graph).
const GRAPH = "var(--graph, #3d5afe)";
const WEAK = "var(--amber, #b8860b)";
const NEW_COLOR = "#f97316";
const CARD_W = 320;
// A card holding the chat's answer is a place to read, so it is wider.
const ANSWER_CARD_W = 380;
const PIN_H = 22;

export function GraphGlyph({ size = 10 }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="6" cy="12" r="2.4" />
            <circle cx="18" cy="6" r="2.4" />
            <circle cx="18" cy="18" r="2.4" />
            <path d="M8.2 11 15.8 7M8.2 13 15.8 17" />
        </svg>
    );
}

// A note's mark: lines of a note.
export function NoteGlyph({ size = 10 }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M5 7h14M5 12h14M5 17h9" />
        </svg>
    );
}

function Avatar({ size = 16, glyph = "graph" }) {
    const Glyph = glyph === "note" ? NoteGlyph : GraphGlyph;
    return (
        <span
            aria-hidden="true"
            className="grid shrink-0 place-items-center rounded-full"
            style={{ width: size, height: size, color: GRAPH, background: `color-mix(in srgb, ${GRAPH} 16%, transparent)` }}
        >
            <Glyph size={Math.round(size * 0.6)} />
        </span>
    );
}

const STRIP_PIN_H = 18;

// One pin. On a lane its sharp corner is its tip, and the tip sits on the point
// it marks, so the pin floats up and to the right of the cell it is about, over
// the cell's neighbours rather than over the cell itself. On the graph strip,
// whose row is barely taller than a pin, it sits round on the turn's mark so it
// never reaches into the strip above.
export function GraphCommentPin({ pin, open = false, unseen = false, onOpen, pinRef = null }) {
    // Filled when a claim here is supported, hollow when none is: how firm, in
    // fill, never in a second hue. A note is always filled: it is a pointer,
    // not a claim to weigh.
    const note = pin.kind === "note";
    const solid = note || pin.tone === "supported";
    const n = pin.claims.length;
    const noun = note ? `note${n === 1 ? "" : "s"} on this trace` : `claim${n === 1 ? "" : "s"} from the chat's knowledge graph`;
    const h = pin.strip ? STRIP_PIN_H : PIN_H;
    const box = pin.strip
        ? { left: pin.x - h / 2, top: pin.y - h / 2, borderRadius: h / 2 }
        : { left: pin.x + 3, top: pin.y - 3 - h, borderRadius: `${h / 2}px ${h / 2}px ${h / 2}px 3px` };
    const button = (
        <button
            type="button"
            ref={pinRef}
            data-graph-pin={pin.id}
            data-pin-kind={note ? "note" : "claim"}
            data-graph-pin-open={open ? "" : undefined}
            data-graph-pin-unseen={unseen ? "" : undefined}
            aria-label={`${n} ${noun}${unseen ? ", new" : ""}`}
            aria-expanded={open}
            onClick={(e) => {
                e.stopPropagation();
                onOpen?.(pin.id);
            }}
            className={`pointer-events-auto absolute grid cursor-pointer place-items-center border-[1.5px] px-[4px] transition-transform duration-100 hover:scale-110 ${pin.strip ? "origin-center" : "origin-bottom-left"}`}
            style={{
                ...box,
                height: h,
                minWidth: h,
                borderColor: GRAPH,
                background: solid ? GRAPH : "var(--bg)",
                color: solid ? "#fff" : GRAPH,
                // The open pin wears a halo, clear of the plane around it.
                boxShadow: open
                    ? `0 0 0 2px var(--bg), 0 0 0 4px ${GRAPH}, 0 2px 6px rgba(0,0,0,0.35)`
                    : "0 1px 3px rgba(0,0,0,0.45)",
            }}
        >
            {n > 1 ? <span className="font-mono text-[10px] font-medium leading-none tabular-nums">{n}</span> : note ? <NoteGlyph /> : <GraphGlyph />}
            {unseen ? (
                <span
                    aria-hidden="true"
                    className="absolute -right-[3px] -top-[3px] h-[8px] w-[8px] rounded-full border-[1.5px] border-[color:var(--bg)]"
                    style={{ background: NEW_COLOR }}
                />
            ) : null}
        </button>
    );
    // A closed pin previews its first claim on hover, the way a comment pin
    // shows the start of its thread; an open one has its card instead.
    if (open || !pin.preview) return button;
    return (
        <HoverTooltip
            wide
            placement="top"
            label={(
                <span className="grid gap-[3px]">
                    <span className="text-[10px] text-[color:var(--muted)]">
                        {note ? "Notes" : "Knowledge graph"} {"\u00b7"} {n} {note ? "note" : "claim"}{n === 1 ? "" : "s"}{unseen ? " \u00b7 new" : ""}
                    </span>
                    <span className="whitespace-normal text-[11px] leading-[1.45]">{pin.preview}</span>
                </span>
            )}
        >
            {button}
        </HoverTooltip>
    );
}

// The pins of one layer (the rows, or the strip stack above them).
export function GraphPinLayer({ pins, isOpen = () => false, isUnseen = () => false, onOpen, bindPin }) {
    return pins.map((pin) => (
        <GraphCommentPin
            key={pin.id}
            pin={pin}
            open={isOpen(pin)}
            unseen={pin.claims.some((id) => isUnseen(id))}
            onOpen={onOpen}
            pinRef={(el) => bindPin?.(pin.id, el)}
        />
    ));
}

function TurnChip({ turn, base = 0, onPick }) {
    return (
        <button
            type="button"
            data-graph-card-turn={turn}
            onClick={() => onPick?.(turn)}
            className="shrink-0 cursor-pointer p-0 font-mono text-[10px] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
        >
            T{base + turn}
        </button>
    );
}

function EvidenceRow({ anchor, base, onPickTurn }) {
    const rel = anchor.relations.slice(0, 3).map((r) => {
        const via = r.via.replace(/_/g, " ");
        return r.reverse ? `${r.label || r.id} ${via}` : `${via} ${r.label || r.id}`;
    });
    const where = anchor.placement ? anchor.placement.detail : `not on the axis: ${anchor.unplaced}`;
    return (
        <div data-graph-card-evidence={anchor.key} className="grid min-w-0 gap-[2px]">
            <div className="flex min-w-0 items-center gap-[6px]">
                <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">{anchor.kind}</span>
                <OverflowTooltip
                    as="code"
                    text={anchor.display || anchor.id}
                    className="min-w-0 truncate rounded-[5px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[5px] py-[1px] font-mono text-[10.5px] leading-[1.35] text-[color:var(--ink)]"
                >
                    {anchor.display || anchor.id}
                </OverflowTooltip>
                {(anchor.placement?.turns || []).slice(0, 3).map((t) => <TurnChip key={t} turn={t} base={base} onPick={onPickTurn} />)}
            </div>
            <div className="min-w-0 text-[10px] leading-[1.4] text-[color:var(--muted)] [overflow-wrap:anywhere]">
                {[where, ...rel].join(" \u00b7 ")}
            </div>
        </div>
    );
}

function ClaimComment({ claim, anchors, base, onPickTurn, onHover }) {
    const color = claim.status === "supported" ? GRAPH : WEAK;
    const cited = claim.anchors.map((k) => anchors.get(k)).filter(Boolean);
    return (
        <div
            data-graph-comment={claim.id}
            onMouseEnter={() => onHover?.(claim.id)}
            onMouseLeave={() => onHover?.(null)}
            className="grid min-w-0 gap-[6px] px-[12px] py-[10px]"
        >
            <div className="flex min-w-0 items-center gap-[7px]">
                <Avatar />
                <span className="text-[11.5px] font-medium text-[color:var(--ink)]">{claim.author === "reader" ? "Reader" : "Chat"}</span>
                {claim.at != null ? <span className="text-[10.5px] text-[color:var(--muted)]">{agoText(claim.at)}</span> : null}
                <span className="ml-auto flex shrink-0 items-baseline gap-[6px] text-[10px]">
                    <span className="text-[color:var(--muted)]">{claim.kind}</span>
                    <span style={{ color }}>{claim.status}{claim.bearing === "context" ? " \u00b7 context" : ""}</span>
                </span>
            </div>
            <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{claim.statement}</p>
            {claim.status !== "supported" && claim.reason ? (
                <p className="m-0 text-[10.5px] leading-[1.45] [overflow-wrap:anywhere]" style={{ color: WEAK }}>{claim.reason}</p>
            ) : null}
            {cited.length ? (
                <div className="grid min-w-0 gap-[6px]">
                    {cited.map((a) => <EvidenceRow key={a.key} anchor={a} base={base} onPickTurn={onPickTurn} />)}
                </div>
            ) : null}
        </div>
    );
}

// One note on the trace, as a comment: who left it (the chat, by its title,
// or a walkthrough), what happened at the turn, and the question it answered
// where it says.
function NoteComment({ note, base, onPickTurn }) {
    return (
        <div data-note-comment={note.id} className="grid min-w-0 gap-[6px] px-[12px] py-[10px]">
            <div className="flex min-w-0 items-center gap-[7px]">
                <Avatar glyph="note" />
                <OverflowTooltip text={note.chat?.title || "Chat"} className="min-w-0 max-w-[180px] truncate text-[11.5px] font-medium text-[color:var(--ink)]">
                    {note.chat?.title || "Chat"}
                </OverflowTooltip>
                {note.at != null ? <span className="shrink-0 text-[10.5px] text-[color:var(--muted)]">{agoText(note.at)}</span> : null}
                <span className="ml-auto flex min-w-0 items-center gap-[6px] text-[10px]">
                    {note.laneLabel ? (
                        <OverflowTooltip text={note.laneLabel} className="min-w-0 max-w-[140px] truncate text-[color:var(--muted)]">
                            {note.laneLabel}
                        </OverflowTooltip>
                    ) : null}
                    {Number.isInteger(note.turn) ? <TurnChip turn={note.turn} base={base} onPick={onPickTurn} /> : null}
                </span>
            </div>
            <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{note.text}</p>
            {note.question ? (
                <p className="m-0 text-[10.5px] leading-[1.45] text-[color:var(--muted)] [overflow-wrap:anywhere]">Asked: {note.question}</p>
            ) : null}
        </div>
    );
}

function NewDivider() {
    return (
        <div data-graph-card-new="" className="flex items-center gap-[8px] px-[12px]">
            <span className="h-px flex-1 bg-[color:var(--line)]" />
            <span className="text-[12px] font-medium" style={{ color: NEW_COLOR }}>New</span>
        </div>
    );
}

// The reader, as a figure in the same round badge the chat wears.
function YouAvatar({ size = 16 }) {
    return (
        <span
            aria-hidden="true"
            className="grid shrink-0 place-items-center rounded-full bg-[color:var(--control-bg)] text-[color:var(--muted)]"
            style={{ width: size, height: size }}
        >
            <svg viewBox="0 0 16 16" width={Math.round(size * 0.62)} height={Math.round(size * 0.62)} fill="currentColor">
                <circle cx="8" cy="5.2" r="2.9" />
                <path d="M2.6 14.2c.4-3 2.7-4.9 5.4-4.9s5 1.9 5.4 4.9z" />
            </svg>
        </span>
    );
}

// What the chat is doing with a reply, in the few words its row has. `reader`:
// a reply typed on a note, which goes to the chat in front.
export function replyStatusText(reply, { reader = false } = {}) {
    const calls = reply.tools ? `${reply.tools} call${reply.tools === 1 ? "" : "s"}` : "";
    switch (reply.state) {
        case "waiting":
            if (reply.activity === "starting") return "starting the chat";
            if (reply.activity === "busy") return "waiting for the chat to finish what it is doing";
            return "sending";
        case "working":
            return [reply.activity || "working", calls].filter(Boolean).join(" \u00b7 ");
        case "answered":
            return calls ? `answered after ${calls}` : "answered";
        case "stopped":
            return "stopped before answering";
        case "failed":
            return "the run failed";
        case "left":
            return "the chat did not start, so your question is waiting in its composer";
        case "missing":
            return reader
                ? "not sent: no chat is open to take it"
                : "not sent: the chat that made these claims is not open in this browser";
        default:
            return "";
    }
}

// One reply and the chat's answer to it, as two comments in the thread.
function ReplyThread({ reply, onOpenChat, reader = false }) {
    const live = reply.state === "waiting" || reply.state === "working";
    const quiet = reply.state === "missing" || reply.state === "left" || reply.state === "failed" || reply.state === "stopped";
    return (
        <div data-graph-card-reply={reply.id} data-reply-state={reply.state} className="grid gap-[8px] border-t border-[color:var(--line-soft)] px-[12px] py-[10px]">
            <div className="grid gap-[5px]">
                <div className="flex items-center gap-[7px]">
                    <YouAvatar />
                    <span className="text-[11.5px] font-medium text-[color:var(--ink)]">You</span>
                    {reply.at != null ? <span className="text-[10.5px] text-[color:var(--muted)]">{agoText(reply.at)}</span> : null}
                </div>
                <p className="m-0 whitespace-pre-wrap text-[12px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{reply.text}</p>
            </div>
            {reply.state === "missing" ? (
                <p className="m-0 text-[10.5px] leading-[1.4] text-[color:var(--muted)]">{replyStatusText(reply, { reader })}</p>
            ) : (
                <div data-graph-card-answer="" className="grid gap-[5px]">
                    <div className="flex min-w-0 items-center gap-[7px]">
                        <Avatar glyph={reader ? "note" : "graph"} />
                        <span className="shrink-0 text-[11.5px] font-medium text-[color:var(--ink)]">Chat</span>
                        {live ? <span aria-hidden="true" className="h-[6px] w-[6px] shrink-0 animate-pulse rounded-full" style={{ background: GRAPH }} /> : null}
                        <span className="min-w-0 truncate text-[10.5px]" style={{ color: quiet ? WEAK : "var(--muted)" }}>{replyStatusText(reply, { reader })}</span>
                        {onOpenChat ? (
                            <button type="button" onClick={onOpenChat} className="ml-auto shrink-0 cursor-pointer p-0 text-[10.5px] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]">
                                {reader ? "show in Reading" : "open the chat"}
                            </button>
                        ) : null}
                    </div>
                    {reply.answer ? (
                        <div className="min-w-0 text-[12px] leading-[1.5] text-[color:var(--ink)]" style={live ? { color: "var(--muted)" } : undefined}>
                            <Markdown text={reply.answer} />
                        </div>
                    ) : null}
                    {reply.error ? (
                        <p className="m-0 text-[10.5px] leading-[1.45] [overflow-wrap:anywhere]" style={{ color: WEAK }}>{reply.error}</p>
                    ) : null}
                </div>
            )}
        </div>
    );
}

// Where the card goes: beside its pin, on whichever side has room, kept inside
// the window. Hidden, not moved, while the pin is scrolled out of the plane.
function placeCard(pinRect, clipRect, cardH, cardW = CARD_W) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const visible = !clipRect || (pinRect.right > clipRect.left && pinRect.left < clipRect.right
        && pinRect.bottom > clipRect.top && pinRect.top < clipRect.bottom);
    let left = pinRect.right + 8;
    if (left + cardW > vw - 8) left = Math.max(8, pinRect.left - 8 - cardW);
    const maxHeight = Math.max(200, Math.min(520, vh - 16));
    const top = Math.max(8, Math.min(pinRect.top - 6, vh - 8 - Math.min(cardH || 0, maxHeight)));
    return { left, top, maxHeight, visible };
}

export function GraphCommentCard({
    pin,
    claims,
    anchors,
    base = 0,
    getPinEl,
    scroller = null,
    isUnseen = () => false,
    // The notes on the trace, for a pin that holds notes rather than claims.
    notes = null,
    replies = [],
    onClose,
    onReply = null,
    onOpenChat = null,
    onOpenList = null,
    onPickTurn = null,
    onHoverClaim = null,
}) {
    const cardRef = useRef(null);
    const bodyRef = useRef(null);
    const [pos, setPos] = useState(null);
    const [text, setText] = useState("");
    const width = replies.some((r) => r.answer) ? ANSWER_CARD_W : CARD_W;
    const lastReply = replies[replies.length - 1] || null;
    // Unseen as it stood when the card opened: reading the card is what makes a
    // claim seen, but the marker has to stay put while it is being read.
    const [firstNew] = useState(() => claims.findIndex((c) => isUnseen(c.id)));
    // The card follows its pin while the pin is in view, and stays where it
    // last was while the pin is scrolled away (a turn link in the card moves
    // the axis), so reading it is never interrupted.
    const place = useCallback(() => {
        const el = getPinEl?.();
        if (!el) return;
        const clip = el.closest("[data-canvas-floating]")?.getBoundingClientRect() || null;
        const next = placeCard(el.getBoundingClientRect(), clip, cardRef.current?.offsetHeight || 0, width);
        setPos((cur) => (next.visible || !cur ? { ...next, visible: true } : cur));
    }, [getPinEl, width]);
    useLayoutEffect(() => { place(); }, [place, pin.id, claims.length, notes?.length, replies.length]);
    // The thread follows the chat's answer as it arrives, unless the reader has
    // scrolled up to read something else.
    const pinnedToEnd = useRef(true);
    useLayoutEffect(() => {
        const el = bodyRef.current;
        if (el && pinnedToEnd.current) el.scrollTop = el.scrollHeight;
    }, [replies.length, lastReply?.answer, lastReply?.state]);
    useEffect(() => {
        let raf = 0;
        const later = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => { raf = 0; place(); });
        };
        scroller?.addEventListener?.("scroll", later, { passive: true });
        window.addEventListener("resize", later);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            scroller?.removeEventListener?.("scroll", later);
            window.removeEventListener("resize", later);
        };
    }, [scroller, place]);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        const onDown = (e) => {
            if (cardRef.current?.contains(e.target)) return;
            if (e.target?.closest?.("[data-graph-pin]")) return;
            onClose?.();
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
        };
    }, [onClose]);

    const send = () => {
        const words = text.trim();
        if (!words || !onReply) return;
        pinnedToEnd.current = true;
        onReply(words);
        setText("");
    };
    const noted = Array.isArray(notes);
    const n = noted ? notes.length : claims.length;
    const turn = pin.turn;
    return createPortal(
        <div
            ref={cardRef}
            role="dialog"
            aria-label={noted ? "Notes on this trace" : "Claims from the chat's knowledge graph"}
            data-graph-card={pin.id}
            className="fixed z-[9998] flex flex-col overflow-hidden rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
            style={{
                width,
                left: pos?.left ?? -9999,
                top: pos?.top ?? -9999,
                maxHeight: pos?.maxHeight,
                visibility: pos ? "visible" : "hidden",
            }}
        >
            <div className="flex min-w-0 flex-none items-center gap-[7px] border-b border-[color:var(--line-soft)] px-[12px] py-[8px]">
                <Avatar size={18} glyph={noted ? "note" : "graph"} />
                <span className="text-[11.5px] font-medium text-[color:var(--ink)]">{noted ? "Notes" : "Knowledge graph"}</span>
                <span className="min-w-0 truncate text-[10.5px] text-[color:var(--muted)]">
                    {n} {noted ? "note" : "claim"}{n === 1 ? "" : "s"}{Number.isInteger(turn) ? ` \u00b7 T${base + turn}` : ""}
                </span>
                <span className="ml-auto flex shrink-0 items-center gap-[10px]">
                    {onOpenList ? (
                        <button type="button" data-graph-card-list="" onClick={onOpenList} className="cursor-pointer p-0 text-[10.5px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                            all claims
                        </button>
                    ) : null}
                    <button type="button" aria-label="Close" onClick={() => onClose?.()} className="cursor-pointer p-0 text-[13px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                        {"\u00d7"}
                    </button>
                </span>
            </div>
            <div
                ref={bodyRef}
                onScroll={(e) => {
                    const el = e.currentTarget;
                    pinnedToEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
                }}
                className="min-h-0 flex-1 overflow-y-auto"
            >
                {noted ? notes.map((note, i) => (
                    <div key={note.id} className={i ? "border-t border-[color:var(--line-soft)]" : ""}>
                        <NoteComment note={note} base={base} onPickTurn={onPickTurn} />
                    </div>
                )) : claims.map((c, i) => (
                    <div key={c.id} className={i ? "border-t border-[color:var(--line-soft)]" : ""}>
                        {i === firstNew ? <div className="pt-[8px]"><NewDivider /></div> : null}
                        <ClaimComment claim={c} anchors={anchors} base={base} onPickTurn={onPickTurn} onHover={onHoverClaim} />
                    </div>
                ))}
                {replies.map((r) => <ReplyThread key={r.id} reply={r} onOpenChat={onOpenChat} reader={noted} />)}
            </div>
            {onReply ? (
                <form
                    className="flex flex-none items-end gap-[8px] border-t border-[color:var(--line-soft)] px-[12px] py-[9px]"
                    onSubmit={(e) => {
                        e.preventDefault();
                        send();
                    }}
                >
                    <textarea
                        rows={1}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Ask the chat about this..."
                        aria-label="Reply to the chat"
                        className="max-h-[96px] min-h-[20px] flex-1 resize-none border-0 bg-transparent p-0 text-[12px] leading-[1.45] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                    />
                    <button
                        type="submit"
                        aria-label="Send to the chat"
                        disabled={!text.trim()}
                        className="grid h-[22px] w-[22px] shrink-0 cursor-pointer place-items-center rounded-full text-white disabled:cursor-default disabled:bg-[color:var(--control-bg)] disabled:text-[color:var(--muted-soft)]"
                        style={text.trim() ? { background: GRAPH } : undefined}
                    >
                        <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" />
                        </svg>
                    </button>
                </form>
            ) : null}
        </div>,
        document.body,
    );
}
